import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteWebhook, type PlainWebhook } from "@/queries/webhook-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { webhooksUrl } from "@/utils/automations";
import { Text } from "@/components/podkit/typography/Text";
import { Banner } from "@/components/flexkit/Banner";

type DeleteWebhookDialogProps = {
    webhook: PlainWebhook;
    open: boolean;
    onClose: () => void;
};

export const DeleteWebhookDialog: FC<DeleteWebhookDialogProps> = ({ webhook, open, onClose }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const deleteWebhook = useDeleteWebhook();

    const webhookName = webhook.metadata?.name || "Unnamed webhook";
    const boundWorkflowCount = webhook.boundWorkflowCount;

    const handleDelete = useCallback(async () => {
        try {
            await deleteWebhook.mutateAsync(webhook.id);
            toast({
                title: "Webhook deleted",
                description: `${webhookName} has been deleted successfully.`,
            });
            onClose();
            navigate(webhooksUrl());
        } catch (error) {
            toast({
                title: "Failed to delete webhook",
                description: formatError(error),
            });
        }
    }, [deleteWebhook, webhook.id, webhookName, toast, onClose, navigate]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="delete-webhook-dialog" data-track-location={TrackLocations.DeleteWebhookDialog}>
                <DialogHeader>
                    <DialogTitle>Delete Webhook</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this webhook? This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-3">
                    {boundWorkflowCount > 0 && (
                        <Banner
                            variant="warning"
                            text={`This webhook is bound to ${boundWorkflowCount} automation${boundWorkflowCount === 1 ? "" : "s"}. Deleting it will remove the webhook trigger from ${boundWorkflowCount === 1 ? "that automation" : "those automations"}.`}
                        />
                    )}
                    <div className="rounded-lg border border-border-base p-3">
                        <Text className="font-medium text-content-primary">{webhookName}</Text>
                        {webhook.spec?.scope && (
                            <Text className="text-sm text-content-secondary">{webhook.spec.scope}</Text>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="delete-webhook-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={deleteWebhook.isPending}
                        data-testid="delete-webhook-dialog-confirm"
                        data-tracking-id="confirm-delete-webhook-delete-webhook-dialog"
                    >
                        Delete Webhook
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
